import { useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Bug, AlertTriangle, Globe, Gauge, ChevronDown, ChevronUp,
  Image, Film, ExternalLink, Monitor, Activity, MousePointerClick, Play,
  Copy,
} from 'lucide-react';
import { ScreenLink as Link } from './permissions/ScreenLink';
import { api } from '../services/api';
import type { AgentReadableContext, PerformanceRating } from '../types';
import { formatRelative } from '../utils/format';

const SessionReplayViewer = lazy(() => import('./SessionReplayViewer'));

interface Props {
  ticketId: string;
}

const RATING_COLORS: Record<PerformanceRating, string> = {
  good: 'text-success-700 bg-success-50',
  needs_attention: 'text-warning-700 bg-warning-50',
  poor: 'text-danger-700 bg-danger-50',
};

function Section({ title, icon: Icon, children, defaultOpen = false }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-surface-300/60 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-3 py-2 bg-surface-50 hover:bg-surface-100 transition-colors text-sm font-medium text-surface-700"
      >
        <span className="flex items-center gap-2">
          <Icon size={14} className="text-surface-500" />
          {title}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="px-3 py-2 text-sm">{children}</div>}
    </div>
  );
}

export default function BugReportContextPanel({ ticketId }: Props) {
  const { t } = useTranslation();
  const [showReplay, setShowReplay] = useState(false);

  const { data, isLoading, error } = useQuery<{ data: AgentReadableContext }>({
    queryKey: ['bug-report-agent-context', ticketId],
    queryFn: () => api.getAgentReadableContext(ticketId) as Promise<any>,
    staleTime: 60_000,
  });

  const { data: reportData } = useQuery({
    queryKey: ['bug-report', ticketId],
    queryFn: () => api.getBugReport(ticketId),
    staleTime: 60_000,
  });

  const screenshotPath = reportData?.data?.context?.screenshot_path;
  const recordingPath = reportData?.data?.context?.recording_path;

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-5 w-40 bg-surface-200 rounded-xs mb-3" />
        <div className="space-y-2">
          <div className="h-4 w-full bg-surface-100 rounded-xs" />
          <div className="h-4 w-3/4 bg-surface-100 rounded-xs" />
        </div>
      </div>
    );
  }

  if (error || !data?.data) return null;

  const ctx = data.data;

  const autoTags: string[] = reportData?.data?.context?.auto_tags || [];
  const agentSummary: string = reportData?.data?.context?.agent_summary || '';
  const duplicateMatch = autoTags.includes('possible-duplicate')
    ? agentSummary.match(/\/tickets\/([0-9a-f-]{36})/)
    : null;
  const duplicateTicketId = duplicateMatch?.[1];

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <Bug size={16} className="text-danger-600" />
        <h3 className="text-sm font-semibold text-surface-700">{t('bugReport.agentContext')}</h3>
      </div>

      <div className="space-y-2">
        {/* Duplicate detection alert */}
        {duplicateTicketId && (
          <div className="flex items-center gap-2 px-3 py-2 bg-warning-50 border border-warning-200 rounded-lg text-sm">
            <Copy size={14} className="text-warning-600 shrink-0" />
            <span className="text-warning-700 font-medium">{t('bugReport.possibleDuplicate')}</span>
            <Link
              to={`/tickets/${duplicateTicketId}`}
              closed="hide"
              className="ms-auto flex items-center gap-1 text-xs text-accent-text hover:underline"
            >
              {t('bugReport.viewOriginal')} <ExternalLink size={12} />
            </Link>
          </div>
        )}

        {/* Summary cards */}
        <Section title={t('bugReport.summary')} icon={Activity} defaultOpen>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-danger-50 rounded-lg p-2 text-center">
              <p className="text-xs text-surface-500">{t('bugReport.errorCount')}</p>
              <p className="text-lg font-semibold text-danger-700 tabular-nums">{ctx.summary.error_count}</p>
            </div>
            <div className="bg-warning-50 rounded-lg p-2 text-center">
              <p className="text-xs text-surface-500">{t('bugReport.warningCount')}</p>
              <p className="text-lg font-semibold text-warning-700 tabular-nums">{ctx.summary.warning_count}</p>
            </div>
            <div className="bg-warning-50 rounded-lg p-2 text-center">
              <p className="text-xs text-surface-500">{t('bugReport.failedRequests')}</p>
              <p className="text-lg font-semibold text-warning-700 tabular-nums">{ctx.summary.failed_requests}</p>
            </div>
            <div className={`rounded-lg p-2 text-center ${RATING_COLORS[ctx.summary.performance_rating]}`}>
              <p className="text-xs text-surface-500">{t('bugReport.performanceRating')}</p>
              <p className="text-sm font-semibold">{t(`bugReport.${ctx.summary.performance_rating}`)}</p>
            </div>
          </div>
          <p className="text-xs text-surface-500 mt-2">
            {t('bugReport.sessionDuration')}: {Math.round(ctx.summary.session_duration_ms / 1000)}s
          </p>
        </Section>

        {/* Errors */}
        {ctx.errors.length > 0 && (
          <Section title={`${t('bugReport.errors')} (${ctx.errors.length})`} icon={AlertTriangle}>
            <div className="space-y-2">
              {ctx.errors.map((err, i) => (
                <div key={i} className="bg-danger-50 border border-danger-100 rounded-lg p-2">
                  <p className="text-xs font-medium text-danger-800 break-all">{err.message}</p>
                  <div className="flex gap-3 mt-1 text-xs text-surface-500">
                    <span>x{err.count}</span>
                    <span>{formatRelative(err.last_seen)}</span>
                  </div>
                  {err.stack_preview && (
                    <pre className="mt-1 text-xs text-surface-500 overflow-x-auto whitespace-pre-wrap leading-tight max-h-16 overflow-y-auto">
                      {err.stack_preview}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Failed requests */}
        {ctx.failed_requests.length > 0 && (
          <Section title={`${t('bugReport.failedRequests')} (${ctx.failed_requests.length})`} icon={Globe}>
            <div className="space-y-1.5">
              {ctx.failed_requests.map((req, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-surface-200 last:border-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-mono font-medium text-surface-600 shrink-0">{req.method}</span>
                    <span className="truncate text-surface-500">{req.url}</span>
                  </div>
                  <span className="font-mono text-danger-700 shrink-0 ms-2">{req.status}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Recent actions */}
        {ctx.recent_actions.length > 0 && (
          <Section title={`${t('bugReport.recentActions')} (${ctx.recent_actions.length})`} icon={MousePointerClick}>
            <div className="space-y-1">
              {ctx.recent_actions.slice(-10).map((action, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                  <span className="px-1.5 py-0.5 rounded-xs bg-surface-100 text-surface-600 font-medium shrink-0">{action.type}</span>
                  <span className="truncate text-surface-500">{action.target || action.route || ''}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Environment */}
        <Section title={t('bugReport.environment')} icon={Monitor}>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-surface-500">Browser</span>
              <span className="text-surface-700 font-medium">{ctx.environment.browser}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-500">OS</span>
              <span className="text-surface-700 font-medium">{ctx.environment.os}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-500">App</span>
              <span className="text-surface-700 font-medium">{ctx.environment.app_version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-500">Route</span>
              <span className="text-surface-700 font-medium font-mono truncate max-w-44">{ctx.environment.route}</span>
            </div>
          </div>
        </Section>

        {/* Performance */}
        {ctx.performance.length > 0 && (
          <Section title={t('bugReport.performance')} icon={Gauge}>
            <div className="space-y-1.5">
              {ctx.performance.map((metric, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-surface-600 font-medium">{metric.metric}</span>
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums text-surface-700">{metric.value.toFixed(1)}ms</span>
                    <span className={`px-1.5 py-0.5 rounded-xs text-xs font-medium ${RATING_COLORS[metric.rating]}`}>
                      {t(`bugReport.${metric.rating}`)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Screenshot */}
        {screenshotPath && (
          <div className="flex items-center gap-2 px-3 py-2 bg-info-50 border border-info-100 rounded-lg text-sm">
            <Image size={14} className="text-info-700" />
            <span className="text-info-700">{t('bugReport.screenshot')}</span>
            <a
              href={`/storage/${screenshotPath}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ms-auto flex items-center gap-1 text-xs text-accent-text hover:underline"
            >
              {t('bugReport.viewScreenshot')} <ExternalLink size={12} />
            </a>
          </div>
        )}

        {/* Recording replay button */}
        {recordingPath && !showReplay && (
          <button
            onClick={() => setShowReplay(true)}
            className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-100 rounded-lg text-sm hover:bg-primary-100 transition-colors w-full"
          >
            <Play size={14} className="text-accent-text" />
            <span className="text-primary-700 font-medium">{t('bugReport.openReplay')}</span>
            <Film size={14} className="text-primary-400 ms-auto" aria-hidden="true" />
          </button>
        )}

        {/* Session replay viewer */}
        {showReplay && (
          <Suspense fallback={
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary-300 border-t-primary-600 rounded-full" />
            </div>
          }>
            <SessionReplayViewer
              ticketId={ticketId}
              onClose={() => setShowReplay(false)}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}
